import {
    ApolloClient,
    createHttpLink,
    FetchResult,
    from,
    InMemoryCache,
    NextLink,
    Observable,
    Operation,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { GraphQLFormattedError } from "graphql";

import AuthStatusNotifier from "~/modules/auth/util/AuthStatusNotifier";
import { mapEditorTypePolicies } from "~/modules/mapEditor/api/cachePolicy";

import { JWT_KEY } from "./constants";

const authLink = setContext((_, { headers }) => {
    const token = localStorage.getItem(JWT_KEY);
    if (!token) return { headers };

    return {
        headers: {
            ...headers,
            authorization: `Bearer ${token}`,
        },
    };
});

function handleUnauthenticatedError(
    error: GraphQLFormattedError,
    operation: Operation,
    forward: NextLink,
): Observable<FetchResult> {
    return new Observable<FetchResult>(observer => {
        AuthStatusNotifier.onEvent()
            .then(result => {
                const subscriber = {
                    next: observer.next.bind(observer),
                    error: observer.error.bind(observer),
                    complete: observer.complete.bind(observer),
                };

                if (result) {
                    // @ts-expect-error Headers comes in as Any from apollo
                    operation.setContext(({ headers }) => ({
                        headers: {
                            ...headers,
                            authorization: `Bearer ${result}`,
                        },
                    }));
                    forward(operation).subscribe(subscriber);
                } else {
                    observer.error(error);
                }
            })
            .catch(e => {
                observer.error(e);
            });
    });
}

const errorLink = onError(({ graphQLErrors, operation, forward }) => {
    if (!graphQLErrors) return;
    for (const error of graphQLErrors) {
        const { message, locations, path, extensions } = error;
        switch (extensions?.code) {
            case "UNAUTHENTICATED":
                return handleUnauthenticatedError(error, operation, forward);
            default:
                // eslint-disable-next-line no-console
                console.log(
                    `[GraphQL error]: Message: ${message}, Code: ${extensions?.code}, Location: ${locations}, Path: ${path}`,
                );
        }
    }
});

const httpLink = createHttpLink({
    uri: import.meta.env.VITE_API_URL,
    credentials: "include",
});

const apolloClient = new ApolloClient({
    link: from([authLink, errorLink, httpLink]),
    cache: new InMemoryCache({
        possibleTypes: {
            // apollo cache is struggling with union type fragments without ids. we explictly list the types here
            GqlFilterOption: [
                "GqlFilterOptionText",
                "GqlFilterOptionSelect",
                "GqlFilterOptionMultiSelect",
                "GqlFilterOptionBoolean",
            ],
        },
        typePolicies: {
            ...mapEditorTypePolicies,
        },
    }),
    defaultOptions: {
        watchQuery: {
            fetchPolicy: "cache-and-network",
        },
    },
});

export default apolloClient;
